import React, {
  ChangeEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import classNames from "classnames";

import { CurrencyDropdown, Input } from "..";
import { Currency } from "../../../types/InvoiceTypes";
import { CURRENCY_MAP } from "../../../constants";
import { formatAmount, maskNumber } from "../../../utils/numbers/money";

type Props = {
  value: InputType;
  onChange?: (value: Partial<InputType>) => void;
  message?: ReactNode;
  variant?: Variant;
  readonlyCurrency?: boolean;
  allowedCurrencies?: Currency[];
  approx?: boolean;
};

export type InputType = {
  amount: number | string;
  currency: Currency;
};

enum Variant {
  PURPLE,
  GREEN,
}

const BACKGROUND_VARIANTS: Record<Variant, string> = {
  [Variant.PURPLE]: "bg-purple-100",
  [Variant.GREEN]: "bg-green-200",
};

export const CurrencyInput: FC<Props> & { variant: typeof Variant } = ({
  value,
  onChange,
  message,
  variant = Variant.PURPLE,
  readonlyCurrency = false,
  allowedCurrencies,
  approx = false,
}) => {
  const { symbol } = CURRENCY_MAP[value.currency];
  const [editable, setEditable] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatAmountInput = useCallback(
    (amount: number) => {
      const formattedAmount = formatAmount(amount, 2, 0).split(".");

      return (
        <div className="flex gap-2 items-end">
          <span>
            {formattedAmount[0]}
            {formattedAmount.length > 1 && (
              <span className="text-xl">.{formattedAmount[1]}</span>
            )}
          </span>
          {approx && <span className="text-sm">approx.</span>}
        </div>
      );
    },
    [approx]
  );

  useEffect(() => {
    if (editable) {
      inputRef.current?.focus();
    }
  }, [editable]);

  const handleChange = (input: Partial<InputType>) => {
    if (onChange) {
      onChange({ ...value, ...input });
    }
  };

  return (
    <div
      className={classNames(
        "flex justify-between p-4 rounded-3xl",
        BACKGROUND_VARIANTS[variant]
      )}
      onClick={() => setEditable(true)}
    >
      <div className="flex flex-col">
        <h4 className="flex items-center font-poppins font-bold gap-2">
          <span className="text-2xl text-violet-200">{symbol}</span>
          <div className="flex text-3xl text-violet-500 items-end">
            {editable ? (
              <Input
                className="border-0 bg-transparent p-0 h-9"
                value={value.amount || ""}
                type="text"
                wrapperClassName="flex"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  e.target.value = maskNumber(e.target.value);
                  handleChange({ amount: e.target.value });
                }}
                onBlur={() => setEditable(false)}
                ref={inputRef}
              />
            ) : (
              <span onClick={() => setEditable(true)}>
                {formatAmountInput(Number(value.amount))}
              </span>
            )}
          </div>
        </h4>

        <span className="text-sm text-violet-300">{message}</span>
      </div>

      <div className="flex items-center min-w-fit">
        <CurrencyDropdown
          name="currency_selector"
          value={value.currency}
          onChange={(currency) => handleChange({ currency })}
          allowedCurrencies={allowedCurrencies}
          readonly={readonlyCurrency}
        />
      </div>
    </div>
  );
};

CurrencyInput.variant = Variant;
